import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ICreateSpendAndStashRequest {
  debitAccountNumber: string;
  savingsAccountNumber: string;
  savingsPercentage: number;
}

interface ICreateSpendAndStashResponse {
  status?: boolean;
  message?: string;
  data?: { id: string };
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateSpendAndStashResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createSpendAndStashAsync = createAsyncThunk(
  'savings/spendAndStash/create',
  async (
    values: ICreateSpendAndStashRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ICreateSpendAndStashResponse>(
        '/savings/spend-and-stash-configuration',
        values,
        getSavingsAuthConfig(state),
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Spend & Stash configured successfully',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateSpendAndStashResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const createSpendAndStashSlice = createSlice({
  name: 'savings/spendAndStash/create',
  initialState,
  reducers: {
    resetCreateSpendAndStash(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSpendAndStashAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createSpendAndStashAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateSpendAndStashResponse;
      })
      .addCase(createSpendAndStashAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateSpendAndStashResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetCreateSpendAndStash } = createSpendAndStashSlice.actions;

export default createSpendAndStashSlice.reducer;
